<template>
  <div class="article-editor">
    <div class="editor-content">

      <!-- 左侧配置面板 -->
      <div class="element-library">
        <div class="panel-header">
          <div class="panel-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            <span>文章配置</span>
          </div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <button class="btn-history" @click="showHistoryDialog = true">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              历史数据
            </button>
            <div v-if="loading" class="generating-badge">
              <div class="generating-dot"></div>
              AI 生成中
            </div>
          </div>
        </div>

        <div class="ai-form-panel">
          <el-form :model="form" :rules="rules" ref="articleForm" label-position="top">
            <el-form-item label="文章标题" prop="title">
              <el-input v-model="form.title" placeholder="例如：基于SpringBoot的在线购物平台设计与实现" clearable maxlength="50" show-word-limit />
            </el-form-item>

            <el-form-item label="关键词">
              <el-input v-model="form.keywords" placeholder="例如：SpringBoot, Vue, 在线购物, 电商平台" clearable />
            </el-form-item>

            <el-form-item label="参考文件模式">
              <div class="template-cards">
                <div :class="['template-card', form.templateMode === 'none' ? 'active' : '']" @click="form.templateMode = 'none'; uploadedFile = null">
                  <div class="template-card-header">
                    <span class="template-card-title">无模板</span>
                    <span :class="['template-radio', form.templateMode === 'none' ? 'checked' : '']"></span>
                  </div>
                  <p class="template-card-desc">无参考模板文件。</p>
                </div>
                <div :class="['template-card', form.templateMode === 'full' ? 'active' : '']" @click="form.templateMode = 'full'">
                  <div class="template-card-header">
                    <span class="template-card-title">全文模板</span>
                    <span :class="['template-radio', form.templateMode === 'full' ? 'checked' : '']"></span>
                  </div>
                  <p class="template-card-desc">老师、学长、学姐分享的完整范文。</p>
                  <el-upload v-if="form.templateMode === 'full'" class="upload-area" action="#" :auto-upload="false" :on-change="onFileChange" :on-remove="onFileRemove" :limit="1" accept=".doc,.docx" drag @click.stop>
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">拖拽或 <em>点击上传</em>，仅支持 doc / docx</div>
                  </el-upload>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="上传 SQL 文件（可选）">
              <el-upload class="upload-area" action="#" :auto-upload="false" :on-change="onSqlFileChange" :on-remove="onSqlFileRemove" :limit="1" accept=".sql" drag>
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">拖拽或 <em>点击上传</em>，仅支持 .sql</div>
              </el-upload>
            </el-form-item>

            <el-form-item label="主要内容描述" prop="description">
              <textarea v-model="form.description" class="func-editor"
                placeholder="请详细描述课题信息，例如：
1. 使用的技术栈（如：SpringBoot、Vue、MySQL等）
2. 系统角色说明（如：管理员、普通用户、商家等）
3. 主要功能列表（如：用户管理、商品管理、订单管理等）
4. 系统特点和创新点
描述越详细，AI生成的内容越精确" />
            </el-form-item>

            <el-form-item label="真实参考文献" prop="references">
              <textarea v-model="form.references" class="func-editor ref-editor"
                placeholder="每行一条，例如：
[1] 张三. 基于SpringBoot的电商系统设计[J]. 计算机应用, 2023.
[2] 李四. Vue.js前端开发实战[M]. 北京: 清华大学出版社, 2022." />
            </el-form-item>
          </el-form>
        </div>

        <div class="panel-footer">
          <label class="agree-label">
            <input type="checkbox" v-model="agreed" class="agree-checkbox" />
            <span>我已阅读并同意：生成的范文仅供参考使用，不作为毕业、学术、发表使用，因使用生成内容产生的一切后果由用户自行承担</span>
          </label>
          <button class="btn-secondary btn-gen-outline" :disabled="outlineLoading || loading" @click="generateOutline">
            <span class="btn-content">
              <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
              {{ outlineLoading ? 'AI 生成目录中...' : 'AI 生成目录' }}
            </span>
            <span class="btn-points">（消耗{{ pointsConfig.articleOutline }}积分）</span>
          </button>
          <button class="btn-ai btn-generate" :disabled="loading || !agreed || tocTreeData.length === 0" @click="generate">
            <span class="btn-content">
              <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
              {{ loading ? 'AI 生成文章中...' : tocTreeData.length === 0 ? '请先生成目录' : 'AI 生成文章' }}
            </span>
            <span class="btn-points">（消耗{{ pointsConfig.articleGeneration }}积分）</span>
          </button>
        </div>
      </div>

      <!-- 右侧结果区域 -->
      <div class="result-area">
        <div class="result-header">
          <div class="result-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            生成结果
          </div>
          <div class="result-meta" v-if="result">
            <span class="word-count">约 {{ wordCount }} 字</span>
            <button class="btn-export" @click="exportWord" :disabled="!result">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="15" height="15">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              导出 Word
            </button>
          </div>
        </div>

        <!-- 结果内容：目录 + 正文 -->
        <div class="result-body">
          <!-- 左侧目录（始终显示） -->
          <div class="toc-panel">
            <div class="toc-header">
              <span class="toc-title">目录</span>
              <button class="toc-add-root-btn" @click="tocAddRoot">+ 新增章节</button>
            </div>
            <div class="toc-tree-wrap">
              <div v-if="outlineLoading" class="toc-loading">
                <div class="toc-spinner"></div>
                <p>AI 正在生成目录...</p>
              </div>
              <div v-else-if="tocTreeData.length === 0" class="toc-empty-state">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="40" height="40">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                <p>点击左侧「AI 生成目录」</p>
                <p class="toc-empty-sub">填写标题和描述后生成</p>
              </div>
              <el-tree v-else
                :data="tocTreeData"
                :props="{ children: 'children', label: 'label' }"
                :expand-on-click-node="false"
                default-expand-all
                node-key="id"
                draggable
                :draggable="!tocIsEditing"
                :allow-drop="tocAllowDrop"
                class="toc-tree"
                @node-click="scrollToHeadingByLabel">
                <span class="toc-tree-node" slot-scope="{ node, data }">
                  <span v-if="!data.editing" @dblclick="tocStartEdit(data)" class="toc-node-label">{{ node.label }}</span>
                  <el-input
                    v-else
                    v-model="data.tempLabel"
                    size="mini"
                    class="toc-edit-input-tree"
                    @blur="tocSaveEdit(data)"
                    @keyup.enter.native="tocSaveEdit(data)"
                    @keyup.esc.native="tocCancelEdit(data)"
                    @mousedown.native.stop
                    @dragstart.native.prevent
                    ref="tocEditInput"
                    @click.native.stop />
                  <span class="toc-node-actions">
                    <el-button type="text" size="mini" @click.stop="tocStartEdit(data)" title="编辑"><i class="el-icon-edit"></i></el-button>
                    <el-button v-if="data.level < 4" type="text" size="mini" @click.stop="tocAppend(data)" title="添加子项"><i class="el-icon-plus"></i></el-button>
                    <el-button type="text" size="mini" @click.stop="tocRemove(node, data)" title="删除"><i class="el-icon-delete"></i></el-button>
                  </span>
                </span>
              </el-tree>
            </div>
          </div>

          <!-- 右侧正文区域 -->
          <div class="result-right">
            <!-- 加载中（还没有内容时） -->
            <div class="loading-state" v-if="loading && !result">
              <div class="loading-spinner"></div>
              <p>AI 正在生成文章，请稍候...</p>
              <p class="loading-sub" v-if="progress">{{ progress }}</p>
              <p class="loading-sub" v-else>预计需要 10~30 分钟，可切换页面，生成完成后回来查看</p>
            </div>

            <!-- 空状态 -->
            <div class="empty-state" v-else-if="!result">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p>填写左侧信息后点击「AI 生成文章」</p>
            </div>

            <!-- 正文 -->
            <div class="result-content" v-else ref="contentArea" @scroll="onContentScroll">
              <div class="progress-bar-wrap" v-if="loading && progress">
                <span class="progress-bar-text">{{ progress }}</span><span class="progress-dots"><span>.</span><span>.</span><span>.</span></span>
              </div>
              <div class="article-body">
                <div
                  v-for="(line, index) in renderedLines"
                  :key="index"
                  :ref="line.isHeading ? 'heading_' + line.headingIndex : null"
                  :class="['article-line', line.isHeading ? 'heading-line heading-' + line.level : line.isFigure ? 'figure-caption-line' : 'body-line']"
                >{{ line.text }}</div>
                <span class="typing-cursor" v-if="loading"></span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 历史数据弹框 -->
    <el-dialog
      title="历史数据"
      :visible.sync="showHistoryDialog"
      width="1200px"
    >
      <div class="history-content">
        <div class="history-description" style="margin-bottom: 12px;">
          <ul>
            <li>• 共有 {{ savedArticles.length }} 个已保存的文章（按保存时间倒序排列）</li>
            <li>• 点击"加载"按钮可以加载对应的文章和目录</li>
            <li>• 系统最多保存20条历史数据记录</li>
          </ul>
        </div>

        <el-table
          :data="paginatedArticles"
          stripe
          style="width: 100%"
          empty-text="暂无保存的历史数据"
        >
          <el-table-column type="index" label="序号" width="60" align="center">
            <template slot-scope="scope">
              {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
            </template>
          </el-table-column>

          <el-table-column label="文章标题" min-width="180" align="center">
            <template slot-scope="scope">
              <span class="diagram-name" :class="{ current: currentArticleId === scope.row.id }">
                {{ scope.row.title || '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="主要内容描述" min-width="200" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.description || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="参考文献" min-width="150" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.references ? scope.row.references.split('\n')[0] + '...' : '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="字数" width="100" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.wordCount || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column label="保存时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="absolute-time">{{ formatTime(scope.row.timestamp) }}</div>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button size="mini" type="primary" @click="loadArticle(scope.row)">加载</el-button>
                <el-button size="mini" type="danger" @click="deleteArticle(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page.sync="currentPage"
            :page-size="pageSize"
            :total="savedArticles.length"
            @current-change="handlePageChange"
            layout="prev, pager, next" />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { baseURL, getHeaders } from '@/utils/request'
import { decrypt } from '@/utils/crypto'

export default {
  name: 'ArticleGenerate',
  data() {
    return {
      form: {
        title: '',
        type: '学术论文',
        description: '',
        keywords: '',
        references: '',
        templateMode: 'none'
      },
      uploadedFile: null,
      sqlFile: null,
      agreed: false,
      activeToc: 0,
      tocEditing: false,
      tocIsEditing: false,
      tocEditable: [],
      tocNodeIdCounter: 100,
      showHistoryDialog: false,
      savedArticles: [],
      currentArticleId: null,
      currentPage: 1,
      pageSize: 10,
      pointsConfig: {
        articleOutline: 2,
        articleGeneration: 15
      },
      rules: {
        title:       [{ required: true, message: '请填写文章标题', trigger: 'blur' }],
        description: [{ required: true, message: '请填写主要内容描述', trigger: 'blur' }],
        references:  [{ required: true, message: '请填写参考文献', trigger: 'blur' }]
      }
    }
  },
  computed: {
    loading() { return this.$store.state.articleGen.loading },
    result() { return this.$store.state.articleGen.result },
    outlineLoading() { return this.$store.state.articleGen.outlineLoading },
    tocTreeData() { return this.$store.state.articleGen.tocTreeData },
    progress() { return this.$store.state.articleGen.progress },
    wordCount() {
      return this.result ? this.result.replace(/\s/g, '').length : 0
    },
    // 解析目录
    toc() {
      if (!this.result) return []
      const items = []
      this.result.split('\n').forEach(line => {
        const t = line.trim()
        const mdMatch = t.match(/^(#{1,4})\s+(.+)/)
        if (mdMatch) { items.push({ level: mdMatch[1].length, text: mdMatch[2] }); return }
        if (/^(第[一二三四五六七八九十百\d]+[章节]\s*[\u4e00-\u9fa5a-zA-Z（(【\[「『《](?!为|主要|介绍|阐述|将|是|从|对|通过|分析|讨论|研究|详细|包含|包括)|摘\s*要|ABSTRACT|Abstract|参考文献|致\s*谢)/.test(t)) {
          items.push({ level: 1, text: t.length > 30 ? t.slice(0, 30) + '…' : t }); return
        }
        if (/^\d+\.\d+\.\d+\.\d+[\s\S]/.test(t)) { items.push({ level: 4, text: t.length > 30 ? t.slice(0, 30) + '…' : t }); return }
        if (/^\d+\.\d+\.\d+[\s\S]/.test(t)) { items.push({ level: 3, text: t.length > 30 ? t.slice(0, 30) + '…' : t }); return }
        if (/^\d+\.\d+[\s\S]/.test(t)) { items.push({ level: 2, text: t.length > 30 ? t.slice(0, 30) + '…' : t }) }
      })
      return items
    },
    // 渲染行，标记标题行
    renderedLines() {
      if (!this.result) return []
      let headingIndex = 0
      return this.result.split('\n').map(line => {
        const t = line.trim()
        // Markdown 标题（兼容 AI 偶尔输出 # 的情况）
        const mdMatch = t.match(/^(#{1,4})\s+(.+)/)
        if (mdMatch) {
          return { text: mdMatch[2], isHeading: true, level: mdMatch[1].length, headingIndex: headingIndex++ }
        }
        // 一级：第X章 / 摘要 / Abstract / 参考文献 / 致谢
        if (/^(第[一二三四五六七八九十百\d]+[章节]\s*[\u4e00-\u9fa5a-zA-Z（(【\[「『《](?!为|主要|介绍|阐述|将|是|从|对|通过|分析|讨论|研究|详细|包含|包括)|摘\s*要|ABSTRACT|Abstract|参考文献|致\s*谢)/.test(t)) {
          return { text: t, isHeading: true, level: 1, headingIndex: headingIndex++ }
        }
        // 四级：1.1.1.1 或 1.1.1.1 标题
        if (/^\d+\.\d+\.\d+\.\d+[\s\S]/.test(t)) {
          return { text: t, isHeading: true, level: 4, headingIndex: headingIndex++ }
        }
        // 三级：1.1.1 标题
        if (/^\d+\.\d+\.\d+[\s\S]/.test(t)) {
          return { text: t, isHeading: true, level: 3, headingIndex: headingIndex++ }
        }
        // 二级：1.1 标题
        if (/^\d+\.\d+[\s\S]/.test(t)) {
          return { text: t, isHeading: true, level: 2, headingIndex: headingIndex++ }
        }
        // 图表占位符：居中显示
        if (/^【(图|表)\d+\.\d+.*】$/.test(t)) {
          return { text: t, isHeading: false, isFigure: true }
        }
        return { text: line, isHeading: false }
      })
    },
    paginatedArticles() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.savedArticles.slice(start, end)
    }
  },
  created() {
    this.increasePageView()
    this.loadPointsConfig()
    this.loadHistoryFromLocal()
    // 从 store 恢复表单数据
    const saved = this.$store.state.articleGen.form
    if (saved && saved.title !== undefined) {
      this.form = { ...saved }
    }
  },
  mounted() {
    // 默认加载最近一个历史记录
    this.loadLatestArticle()
  },
  watch: {
    form: {
      deep: true,
      handler(val) {
        this.$store.commit('setArticleGenForm', val)
      }
    },
    toc(val) {
      // 仅在非编辑状态下同步，避免覆盖用户修改
      if (!this.tocEditing) {
        this.tocEditable = val.map(item => ({ ...item }))
      }
    }
  },
  methods: {
    // 加载积分配置
    async loadPointsConfig() {
      try {
        const response = await this.request.get('/ai/points-config')
        if (response.code === '200' && response.data) {
          this.pointsConfig = response.data
        }
      } catch (error) {
        console.error('加载积分配置失败:', error)
      }
    },

    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'articleGenerate' }
      }).catch(() => {})
    },
    async generateOutline() {
      if (!this.form.title) { this.$message.warning('请先填写文章标题'); return }
      if (!this.form.description) { this.$message.warning('请先填写主要内容描述'); return }
      if (this.form.templateMode === 'full' && !this.uploadedFile) {
        this.$message.warning('请先上传全文模板文件')
        return
      }

      // 添加积分确认提示
      try {
        await this.$confirm(`此操作将消耗${this.pointsConfig.articleOutline}积分，是否继续？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        this.$message.info('已取消操作')
        return
      }

      this.$store.commit('setOutlineLoading', true)
      try {
        const formData = new FormData()
        formData.append('title', this.form.title)
        formData.append('description', this.form.description)
        formData.append('keywords', this.form.keywords || '')
        formData.append('templateMode', this.form.templateMode)
        if (this.uploadedFile) formData.append('file', this.uploadedFile)

        const res = await fetch(baseURL + '/ai/generateOutline', {
          method: 'POST',
          headers: getHeaders(null),
          body: formData
        })
        if (!res.ok) throw new Error('请求失败')

        // 获取响应文本
        const responseText = await res.text()

        let data

        // 先尝试直接解析 JSON
        try {
          const parsed = JSON.parse(responseText)

          // 如果解析结果是字符串，说明是加密的数据
          if (typeof parsed === 'string') {
            console.log('🔐 检测到加密数据，开始解密...')
            const decrypted = decrypt(parsed)
            data = JSON.parse(decrypted)
          } else {
            // 如果是对象，说明未加密
            data = parsed
          }
        } catch (error) {
          throw new Error('响应解析失败')
        }

        if (data.code === '200' && data.data) {
          this.$store.commit('setTocTreeData', data.data)
          this.$message.success('目录生成成功，可在右侧编辑')
          // 刷新积分
          this.refreshPoints()
        } else {
          throw new Error(data.msg || '生成失败')
        }
      } catch (e) {
        this.$message.error('目录生成失败：' + e.message)
      } finally {
        this.$store.commit('setOutlineLoading', false)
      }
    },
    fixCitationOrder() {
      let text = this.$store.state.articleGen.result
      const oldToNew = {}
      let nextNum = 1
      text = text.replace(/\[(\d+)\]/g, (match, num) => {
        if (!oldToNew[num]) {
          oldToNew[num] = nextNum++
        }
        return '[' + oldToNew[num] + ']'
      })
      this.$store.commit('setArticleGenResult', text)
    },
    tocStartEdit(data) {      this.tocIsEditing = true
      this.$set(data, 'editing', true)
      this.$set(data, 'tempLabel', data.label)
      this.$nextTick(() => {
        const inputs = this.$refs.tocEditInput
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[inputs.length - 1] : inputs
          input.focus()
        }
      })
    },
    tocSaveEdit(data) {
      if (data.tempLabel && data.tempLabel.trim()) {
        data.label = data.tempLabel.trim()
      }
      this.tocCancelEdit(data)
    },
    tocCancelEdit(data) {
      this.tocIsEditing = false
      this.$set(data, 'editing', false)
      this.$delete(data, 'tempLabel')
    },
    tocAppend(data) {
      const child = { id: ++this.tocNodeIdCounter, label: '新条目', level: data.level + 1 }
      if (!data.children) this.$set(data, 'children', [])
      data.children.push(child)
    },
    tocRemove(node, data) {
      const parent = node.parent
      const children = parent.data.children || parent.data
      const index = children.findIndex(d => d.id === data.id)
      children.splice(index, 1)
    },
    tocAddRoot() {
      const newData = [...this.tocTreeData, { id: ++this.tocNodeIdCounter, label: '新章节', level: 1, children: [] }]
      this.$store.commit('setTocTreeData', newData)
    },
    tocAllowDrop(draggingNode, dropNode, type) {
      // 不允许超过3层
      if (type === 'inner') {
        return dropNode.level < 4
      }
      return true
    },
    scrollToHeadingByLabel(data) {
      const idx = this.renderedLines.findIndex(l => l.isHeading && l.text === data.label)
      if (idx === -1) return
      const line = this.renderedLines[idx]
      this.$nextTick(() => {
        const refs = this.$refs['heading_' + line.headingIndex]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && this.$refs.contentArea) {
          this.$refs.contentArea.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' })
        }
      })
    },
    async exportWord() {
      if (!this.result) return
      try {
        const response = await fetch(baseURL + '/ai/exportWord', {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ title: this.form.title || '文章', content: this.result, outline: JSON.stringify(this.tocTreeData) })
        })
        if (!response.ok) throw new Error('导出失败')
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = (this.form.title || '文章') + '.docx'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败：' + e.message)
      }
    },
    scrollToHeading(index) {
      this.activeToc = index
      this.$nextTick(() => {
        const refs = this.$refs['heading_' + index]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && this.$refs.contentArea) {
          this.$refs.contentArea.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' })
        }
      })
    },
    onContentScroll() {
      const area = this.$refs.contentArea
      if (!area) return
      const scrollTop = area.scrollTop
      let active = 0
      this.renderedLines.forEach((line, i) => {
        if (!line.isHeading) return
        const refs = this.$refs['heading_' + line.headingIndex]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && el.offsetTop - 32 <= scrollTop) {
          active = line.headingIndex
        }
      })
      this.activeToc = active
    },
    onFileChange(file) { this.uploadedFile = file.raw },
    onFileRemove() { this.uploadedFile = null },
    onSqlFileChange(file) { this.sqlFile = file.raw },
    onSqlFileRemove() { this.sqlFile = null },

    async generate() {
      const valid = await new Promise(resolve => this.$refs.articleForm.validate(resolve))
      if (!valid) {
        this.$message.warning('请检查必填项是否填写完整')
        return
      }
      if (this.form.templateMode === 'full' && !this.uploadedFile) {
        this.$message.warning('请先上传全文模板文件')
        return
      }

      // 添加积分确认提示
      try {
        await this.$confirm(`此操作将消耗${this.pointsConfig.articleGeneration}积分，是否继续？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        this.$message.info('已取消操作')
        return
      }

      this.$store.commit('setArticleGenLoading', true)
      this.$store.commit('clearArticleGen')
      this.$store.commit('setArticleGenTitle', this.form.title)
      this.$store.commit('setArticleGenLoading', true)

      try {
        const formData = new FormData()
        formData.append('title', this.form.title)
        formData.append('description', this.form.description)
        formData.append('keywords', this.form.keywords || '')
        formData.append('references', this.form.references)
        formData.append('outline', JSON.stringify(this.tocTreeData))
        formData.append('templateMode', this.form.templateMode)
        if (this.uploadedFile) formData.append('file', this.uploadedFile)
        if (this.sqlFile) formData.append('sqlFile', this.sqlFile)

        const response = await fetch(baseURL + '/ai/generateArticleStream', {
          method: 'POST',
          headers: getHeaders(null),
          body: formData
        })

        if (!response.ok) throw new Error('请求失败：' + response.status)

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const data = line.slice(6)
            if (data.trim() === '[DONE]') {
              this.$store.commit('appendArticleGenResult', '')
              // 保存到历史记录
              this.saveToHistory()
              // 刷新积分
              this.refreshPoints()
              return
            }
            if (data.trim().startsWith('[ERROR]')) {
              this.$message.error(data.trim().replace('[ERROR] ', ''))
              return
            }
            // 进度提示：显示为加载状态文字，不写入正文
            if (data.trim().startsWith('[PROGRESS]')) {
              const msg = data.replace(/\\n\\n$/, '').replace('[PROGRESS] ', '').trim()
              this.$store.commit('setArticleGenProgress', msg)
              continue
            }
            this.$store.commit('appendArticleGenResult', data.replace(/\\n/g, '\n'))
          }
        }
      } catch (e) {
        this.$message.error('请求失败：' + e.message)
      } finally {
        this.$store.commit('setArticleGenLoading', false)
      }
    },

    // 刷新积分
    refreshPoints() {
      // 使用全局方法刷新积分
      this.$refreshPoints()
    },

    // 历史记录相关方法
    loadHistoryFromLocal() {
      const history = JSON.parse(localStorage.getItem('articleHistory') || '[]')
      this.savedArticles = history
    },

    loadLatestArticle() {
      if (this.savedArticles.length === 0) return
      const latest = this.savedArticles[0]
      this.loadArticleData(latest)
    },

    loadArticleData(article) {
      // 加载表单数据
      this.form = {
        title: article.title,
        type: article.type || '学术论文',
        description: article.description,
        keywords: article.keywords,
        references: article.references,
        templateMode: article.templateMode || 'none'
      }

      // 加载目录
      if (article.tocTreeData) {
        this.$store.commit('setTocTreeData', article.tocTreeData)
      }

      // 加载生成的内容
      if (article.result) {
        this.$store.commit('setArticleGenResult', article.result)
      }

      this.currentArticleId = article.id
    },

    saveToHistory() {
      const article = {
        id: Date.now(),
        timestamp: Date.now(),
        title: this.form.title,
        type: this.form.type,
        description: this.form.description,
        keywords: this.form.keywords,
        references: this.form.references,
        templateMode: this.form.templateMode,
        tocTreeData: this.tocTreeData,
        result: this.result,
        wordCount: this.wordCount
      }

      const history = JSON.parse(localStorage.getItem('articleHistory') || '[]')
      history.unshift(article)

      // 只保留最近20条
      if (history.length > 20) {
        history.splice(20)
      }

      localStorage.setItem('articleHistory', JSON.stringify(history))
      this.savedArticles = history
      this.currentArticleId = article.id
    },

    deleteArticle(article) {
      this.$confirm('确定要删除这篇文章吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const history = JSON.parse(localStorage.getItem('articleHistory') || '[]')
        const index = history.findIndex(item => item.id === article.id)
        if (index > -1) {
          history.splice(index, 1)
          localStorage.setItem('articleHistory', JSON.stringify(history))
          this.savedArticles = history
          this.$message.success('删除成功')
        }
      }).catch(() => {})
    },

    loadArticle(article) {
      this.$confirm('确定要加载这篇文章吗？当前内容将被替换。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loadArticleData(article)
        this.showHistoryDialog = false
        this.$message.success('文章加载成功')
      }).catch(() => {})
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    formatTime(timestamp) {
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.article-editor {
  height: calc(100vh - 118px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ===== 上方配置面板 ===== */
.form-col-full {
  flex: 1;
  width: 100%;
}

/* 模板选择卡片 */
.template-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.template-card {
  flex: 1;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
  min-width: 0;
}

.template-card:hover { border-color: #4f46e5; background: #fafafe; }
.template-card.active { border-color: #4f46e5; background: #f5f3ff; }

.template-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.template-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.template-radio {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  flex-shrink: 0;
  transition: all 0.2s;
  position: relative;
}

.template-radio.checked {
  border-color: #4f46e5;
  background: #4f46e5;
  box-shadow: inset 0 0 0 3px #fff;
}

.template-card-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 8px;
}

.sql-card {
  border-color: #e5e7eb;
  cursor: default;
}
.sql-card:hover { border-color: #10b981; background: #f0fdf4; }

.sql-form-item {
  margin-bottom: 0;
  align-self: flex-start;
}

.element-library {
  width: 380px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
  border-right: 1px solid #d1d5db;
  overflow: hidden;
}

.panel-header {
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.generating-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #10b981;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 3px 10px;
}

.generating-dot {
  width: 7px;
  height: 7px;
  background: #10b981;
  border-radius: 50%;
  animation: blink 1s step-end infinite;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.panel-icon {
  width: 22px;
  height: 22px;
  color: #4f46e5;
}

.ai-form-panel {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.ai-form-panel .el-form-item {
  margin-bottom: 16px;
}

.ai-form-panel .el-form-item__label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  padding-bottom: 4px;
  line-height: 1.4;
}

/* 表单横排布局 */
.form-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.form-row-bottom {
  margin-top: 10px;
}

.form-col {
  flex: 1;
  min-width: 0;
}

.form-col-wide {
  flex: 2;
  min-width: 0;
}

.form-col-half {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.form-col-half .el-form-item__content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.func-editor {
  width: 100%;
  height: 150px;
  background: #ffffff;
  color: #333;
  font-family: inherit;
  font-size: 13px;
  padding: 8px 12px;
  resize: none;
  border: 1px solid #dcdfe6;
  outline: none;
  line-height: 1.6;
  border-radius: 4px;
  box-sizing: border-box;
}

.func-editor:focus { border-color: #4f46e5; }

.ref-editor { height: 180px; }

.panel-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agree-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  cursor: pointer;
}

.agree-checkbox {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #4f46e5;
  cursor: pointer;
}

.btn-generate { width: 100%; }

.btn-gen-outline {
  width: 100%;
  background: #4f46e5;
  color: #fff;
  border: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-gen-outline .ai-icon { width: 26px; height: 26px; object-fit: contain; }
.btn-gen-outline:hover:not(:disabled) { background: #4338ca; }
.btn-gen-outline:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-points {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
}

.upload-area { width: 100%; overflow: hidden; }
.upload-area >>> .el-upload { width: 100%; }
.upload-area >>> .el-upload-dragger {
  width: 100%;
  box-sizing: border-box;
  height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0 12px;
}
.upload-area >>> .el-upload-dragger .el-icon-upload {
  font-size: 20px;
  margin: 0;
  line-height: 1;
  color: #c0c4cc;
}
.upload-area >>> .el-upload-dragger .el-upload__text {
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
}

/* 字数下拉底部自定义输入 */
.custom-word-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  list-style: none;
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 1;
}

.custom-word-native-input {
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 16px;
  font-size: 13px;
  outline: none;
  color: #333;
}
.custom-word-native-input:focus { border-color: #4f46e5; }

.custom-word-btn {
  height: 32px;
  padding: 0 16px;
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
}
.custom-word-btn:hover { background: #4338ca; }

.btn-primary, .btn-secondary, .btn-ai {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.btn-ai {
  background: #10b981;
  color: #fff;
}
.btn-ai:hover:not(:disabled) { background: #059669; }
.btn-ai:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-ai .ai-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

/* ===== 右侧结果区域 ===== */
.result-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
  margin-left: 10px;
  border-left: 1px solid #d1d5db;

}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex-shrink: 0;
}

.result-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.word-count {
  font-size: 14px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
}

.btn-export {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  font-size: 13px;
  color: #fff;
  background: #4f46e5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-export:hover:not(:disabled) { background: #4338ca; }
.btn-export:disabled { opacity: 0.4; cursor: not-allowed; }

.result-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-sub { font-size: 12px; color: #9ca3af; margin-top: -4px; }

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  background: #fafafa;
}

.empty-state svg {
  color: #d1d5db;
}

.empty-state p {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

/* ===== 结果区内部：目录 + 正文 ===== */
.result-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.result-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.toc-empty {
  padding: 16px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  line-height: 1.6;
}

.toc-panel {
  width: 360px;
  flex-shrink: 0;
  border-right: 1px solid #e5e7eb;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.toc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 8px;
  flex-shrink: 0;
}

.toc-title {
  font-size: 16px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.toc-tree-wrap {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
  position: relative;
}

.toc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
  color: #6b7280;
  font-size: 13px;
}

.toc-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 8px;
  padding: 20px;
  background: #fafafa;
}

.toc-empty-state svg {
  color: #d1d5db;
}

.toc-empty-state p {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.toc-empty-sub {
  font-size: 14px !important;
  color: #9ca3af;
}

.toc-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.toc-tree {
  background: transparent;
  border: none;
}

.toc-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  overflow: hidden;
  width: 100%;
}

.toc-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
}
.toc-node-label:hover { background: #eef2ff; color: #4f46e5; }

.toc-node-actions {
  display: inline-flex;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.toc-tree-node:hover .toc-node-actions { opacity: 1; }
.toc-node-actions .el-button { padding: 2px 4px; }
.toc-node-actions .el-button i { font-size: 12px; }

.toc-edit-input-tree { width: 140px; margin-right: 6px; }
.toc-edit-input-tree >>> .el-input__inner { height: 26px; line-height: 26px; font-size: 12px; }

.toc-add-root-btn {
  font-size: 14px;
  color: #4f46e5;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 0;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}
.toc-add-root-btn:hover { background: #e0e7ff; }

.result-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  background: #fff;
}

.article-body { max-width: 720px; padding: 0 24px; }

.body-line {
  font-family: 'Microsoft YaHei', '微软雅黑', sans-serif;
  font-size: 15px;
  line-height: 1.9;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 1.9em;
}

.heading-line {
  font-family: 'Microsoft YaHei', '微软雅黑', sans-serif;
  font-weight: 700;
  color: #111827;
  margin-top: 24px;
  margin-bottom: 8px;
}
.figure-caption-line {
  font-family: 'Microsoft YaHei', '微软雅黑', sans-serif;
  font-size: 14px;
  line-height: 2;
  color: #374151;
  text-align: center;
  white-space: pre-wrap;
}
.heading-1 { font-size: 22px; text-align: center; }
.heading-2 { font-size: 18px; }
.heading-3 { font-size: 16px; }
.heading-4 { font-size: 15px; }

.typing-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background: #4f46e5;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: blink 0.8s step-end infinite;
}
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

.progress-bar-wrap {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.progress-bar-text {
  font-size: 13px;
  color: #1d4ed8;
}
.progress-dots span {
  display: inline-block;
  font-size: 13px;
  color: #1d4ed8;
  animation: dotBounce 1.2s infinite;
}
.progress-dots span:nth-child(2) { animation-delay: 0.2s; }
.progress-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes dotBounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
}

/* 历史记录对话框样式 */
.history-content {
  padding: 0;
}

.history-description {
  background: #f9fafb;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.history-description ul {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.history-description li {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.8;
}

.diagram-name {
  font-weight: 500;
  color: #111827;
}

.diagram-name.current {
  color: #2563eb;
  font-weight: 600;
}

.absolute-time {
  font-size: 13px;
  color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 16px 0;
}

.btn-history {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #4f46e5;
  background: #fff;
  border: 1px solid #4f46e5;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-history:hover {
  background: #4f46e5;
  color: #fff;
}

.btn-history svg {
  width: 16px;
  height: 16px;
}
</style>
